//@ts-check
import ky, { isNetworkError, isTimeoutError } from "ky";
import { getfetchopts } from "$lib/utils/utils.js";
import { showOverlay } from "$lib/overlay.svelte.js";
import { apiBase } from "$lib/env.js";
import { forgetDeviceToken } from "$lib/native.js";
import { getTranslations } from "$lib/utils/locale.svelte.js";
import common from "$lib/messages/common.js";
import { clearOutbox } from "$lib/outbox.js";
import { clearLog } from "$lib/captureprobe.js";

// Relative on the web; the app host inside the iOS app ($lib/env.js).
const basePath = apiBase();

// Reactive session state. `user` is null when signed out; otherwise the profile. This project has
// no roles or permissions — a signed-in user is a user, and every handler scopes its reads and
// writes to the caller's own records (see guides/AUTHENTICATION_FLOW.md).
//
// `hint` is a NON-AUTHORITATIVE "probably signed in" flag, cached locally (IndexedDB) so public
// pages can label nav (footer, mobile drawer) without calling the auth API. It is for presentation
// only — never gate access on it. The authoritative check is /api/auth/me (loadSession), run when
// entering the signed-in area, which corrects a stale hint.
//
// `offline` means /me could not be reached and `user` is the profile this device last saw. The app
// opens on it rather than bouncing to a sign-in page that cannot load either — a capture made in
// a shop with no signal waits in the outbox. The signed-in layout checks again once the network
// is back, and a session that really has lapsed is sent to sign in then.
export const auth = $state({
  loading: true,
  loaded: false,
  user: null,
  hint: false,
  offline: false,
});

const AUTH_HINT_KEY = "auth_hint";
const USER_KEY = "user";

/**
 * Persist (and reflect) whether the user appears signed in — for nav labeling only. Best-effort.
 * @param {boolean} signedIn
 */
function rememberSignedIn(signedIn) {
  auth.hint = signedIn;
  idbSet(AUTH_HINT_KEY, signedIn).catch((err) => console.warn("Could not persist auth hint", err));
}

/**
 * Hydrate the signed-in hint from local storage WITHOUT any network call. Call this at app boot on
 * public pages so nav reflects signed-in state without hitting /api/auth/me. Authoritative session
 * loading is still loadSession(), done when entering the signed-in area.
 */
export async function hydrateAuthHint() {
  try {
    auth.hint = (await idbGet(AUTH_HINT_KEY)) === true;
  } catch {
    auth.hint = false;
  }
}

/**
 * Whether this device was signed in last time with the app's first questions behind it: the hint,
 * and the profile kept from then saying the age question is answered and there is a handle. Lets
 * the signed-in layout start Home's requests before /me answers
 * (wip-archive/app-start/APP_START.md, step 3). Like the hint, never a reason to show anything.
 *
 * @returns {Promise<boolean>}
 */
export async function readyLastTime() {
  if (!auth.hint) return false;
  const cached = await idbGet(USER_KEY).catch(() => null);
  return !!cached && cached.ageAnswered !== false && cached.handle !== null;
}

/**
 * Mark the user as signed in locally (e.g. right after the auth callback exchange).
 * @param {boolean} signedIn
 */
export function persistAuthHint(signedIn) {
  rememberSignedIn(signedIn);
}

// In-flight de-dupe: the root layout loads the session at app boot and the signed-in layout also
// ensures it's loaded; concurrent callers share the same request rather than firing /me twice.
let inflight = null;

/**
 * Load the current session from /api/auth/me. The session cookie is sent automatically
 * (same-origin on the apex). A 401 means signed-out — leaves `user` null without throwing.
 * Concurrent calls return the same in-flight promise; once settled, a later call re-fetches.
 * @returns {Promise<void>}
 */
export function loadSession() {
  if (inflight) return inflight;
  inflight = (async () => {
    auth.loading = true;
    try {
      const fetchopts = await getfetchopts();
      const user = await ky.get(`${basePath}/api/auth/me`, fetchopts).json();
      auth.user = user;
      auth.offline = false;
      rememberSignedIn(true);
      idbSet(USER_KEY, user).catch((err) => console.warn("Could not keep the profile", err));
      // FUTURE — session/config persistence (planning marker, no action needed yet):
      // Today the profile lives only in memory and is re-fetched from /me on every
      // full page load. Once the app caches heavier user config/session state, persist it to the
      // 'tingthings-auth' IndexedDB store (idbGet/idbSet below) and hydrate from it here for instant
      // loads / offline PWA use, then refresh from /me in the background. See the matching marker
      // in routes/auth/callback/+page.svelte.
    } catch (err) {
      // No network is not the same as signed out. Only a device that was signed in last time,
      // and still has the profile, opens without the server's word for it.
      if (isNetworkError(err) || isTimeoutError(err) || !navigator.onLine) {
        const cached = auth.hint ? await idbGet(USER_KEY).catch(() => null) : null;
        if (cached) {
          auth.user = cached;
          auth.offline = true;
          return;
        }
      }
      auth.user = null;
      auth.offline = false;
      rememberSignedIn(false);
      if (err.isautherr) {
        // Inside the iOS app, a refused token was revoked or has run out: drop it, so the next
        // sign-in starts clean. A no-op on the web.
        await forgetDeviceToken();
      } else {
        console.error("Failed to load session", err);
      }
    } finally {
      auth.loading = false;
      auth.loaded = true;
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Begin the sign-in flow: bounce to the apex /login page with a return URL.
 * @param {string} [returnUrl]
 */
export function goauth(returnUrl) {
  // Search params are part of where you were going. A share arrives as /share?url=... and the
  // payload lives entirely in the query — sending someone to sign in and returning them to a bare
  // /share would drop the thing they were trying to save.
  const here = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
  const target = returnUrl || here;
  window.location.href = `/login?redirect=${encodeURIComponent(target)}`;
}

let refused = false;

/**
 * An API call was refused with a 401 while the session was in use — revoked from the devices
 * list, or run out. Several calls can be refused at once (the things and the lists load
 * together), so it acts on the first.
 *
 * The identity API has the last word: `loadSession` asks `/me`, and on its 401 forgets the device
 * token. Only then is the person sent to sign in, and brought back here afterwards. Were one API
 * ever to refuse a session the identity API still accepts, sending them to sign in would bring
 * them straight back, round and round; instead the refused call's own error shows.
 *
 * Nothing waiting is lost: a capture is in the outbox before it is sent, and comes back once the
 * same person signs in again.
 */
export async function sessionRefused() {
  if (refused) return;
  refused = true;
  await loadSession();
  if (auth.user) {
    refused = false;
    return;
  }
  goauth();
}

/** Sign out: delete the server session, clear local state, return home. */
export async function logout() {
  // Cloak the page immediately so the click registers, then keep it up through the navigation.
  showOverlay(getTranslations(common)("signing_out"));
  try {
    const fetchopts = await getfetchopts();
    await ky.get(`${basePath}/api/auth/logout`, fetchopts).json();
  } catch (err) {
    console.warn("Logout request failed", err);
  }
  // Inside the iOS app the logout call revoked this device's token; forget it here too.
  await forgetDeviceToken();
  auth.user = null;
  rememberSignedIn(false);
  await idbSet(USER_KEY, null).catch(() => {});
  // Full navigation unloads the page (and the overlay with it) — no hideOverlay needed.
  window.location.href = "/";
}

/**
 * Forget the account on this device, once the server has deleted it: the device token inside the
 * iOS app (the server has already revoked it), the profile and the signed-in hint, the captures
 * still waiting to be sent and their photos, and lab's capture log. The session cookie went with
 * the account. The device id stays: it names this browser, not the person.
 */
export async function forgetEverything() {
  await forgetDeviceToken();
  auth.user = null;
  rememberSignedIn(false);
  await idbSet(USER_KEY, null).catch(() => {});
  await clearOutbox().catch((err) => console.warn("Could not clear the outbox", err));
  clearLog();
}

// ---- IndexedDB-backed local store (survives across PWA sessions) ----
// Currently holds only device_id. This is the intended home for future durable session/user-config
// keys (see the planning markers in loadSession above and routes/auth/callback/+page.svelte): the
// generic kv store + idbGet/idbSet below already support adding keys without schema changes.
const DB_NAME = "tingthings-auth";
const STORE = "kv";
const DEVICE_KEY = "device_id";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(key) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(key);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      })
  );
}

function idbSet(key, value) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(value, key);
        tx.oncomplete = () => resolve(value);
        tx.onerror = () => reject(tx.error);
      })
  );
}

/** Get (creating if needed) the persistent device id for this browser/PWA install. */
export async function getDeviceId() {
  try {
    let id = await idbGet(DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      await idbSet(DEVICE_KEY, id);
    }
    return id;
  } catch (err) {
    console.warn("IndexedDB unavailable; using ephemeral device id", err);
    return crypto.randomUUID();
  }
}
